import { useEffect } from 'react'
import { useLocation, useNavigate } from 'router-v6'
import { ACI_RETURN_TO_KEY } from 'v2/pages/settings/integrations/oauth-redirect'
import { SuspenseLoader } from 'v2/shared/components/suspense-loader'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import { logger } from 'v2/utils/logger'
import { errorToast, successToast } from 'v2/utils/toasts'
import { parseAciOAuthCompletion, safeReturnTo } from './aci-oauth-landing-utils'
import { useConfirmAciConnectionMutation } from './queries'

interface AciOAuthLandingDependencies {
  confirmAciConnection: (ownerId: string) => Promise<RExternalOrgApp | undefined>
  notifySuccess: typeof successToast
  notifyError: typeof errorToast
  logError: (message: string) => void
}

interface AciOAuthLandingProps {
  dependencies?: AciOAuthLandingDependencies
}

const defaultLogError = (message: string) => logger.error(message)

/** Where the bridge sends the browser back after the provider's consent screen. */
export const AciOAuthLanding = ({ dependencies }: AciOAuthLandingProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { mutateAsync: confirmAciConnection } = useConfirmAciConnectionMutation()
  const confirmConnection = dependencies?.confirmAciConnection ?? confirmAciConnection
  const notifySuccess = dependencies?.notifySuccess ?? successToast
  const notifyError = dependencies?.notifyError ?? errorToast
  const logError = dependencies?.logError ?? defaultLogError
  // Depend on the primitive, not a field read off the mutable location object
  // (react-doctor/no-mutable-in-deps).
  const locationSearch = location.search

  useEffect(() => {
    let cancelled = false
    const returnTo = safeReturnTo(sessionStorage.getItem(ACI_RETURN_TO_KEY))

    const done = () => {
      if (cancelled) return
      sessionStorage.removeItem(ACI_RETURN_TO_KEY)
      navigate(returnTo)
    }

    const completeConnect = async () => {
      const completion = parseAciOAuthCompletion(locationSearch)

      if (completion.kind === 'cancelled') {
        notifyError({
          title: 'Connection cancelled',
          description: 'Authorization was cancelled. You can reconnect when you are ready.',
        })
        done()
        return
      }

      if (completion.kind === 'error') {
        notifyError({
          title: 'Connection error',
          description: `Authorization did not complete (${completion.errorCode}). Please reconnect.`,
        })
        done()
        return
      }

      try {
        const match = await confirmConnection(completion.ownerId)
        if (cancelled) return
        if (match) {
          notifySuccess({
            title: 'Connected',
            description: `${match.name} connected successfully.`,
          })
        } else {
          notifyError({
            title: 'Connection error',
            description: 'The connection was not found. Please reconnect.',
          })
        }
      } catch {
        if (cancelled) return
        logError('ACI bridge connect confirmation failed')
        notifyError({
          title: 'Connection error',
          description: 'Could not confirm the connection. Please try again.',
        })
      } finally {
        done()
      }
    }

    completeConnect()

    return () => {
      cancelled = true
    }
  }, [confirmConnection, locationSearch, logError, navigate, notifyError, notifySuccess])

  return <SuspenseLoader text="Finishing your connection..." />
}
